import {
  localizeEgg,
  localizeItem,
  localizeWeapon,
} from "../content-i18n";
import { EGG_BY_ID, ITEM_BY_ID, WEAPON_BY_ID } from "../core/content";
import type { BattleState } from "../core/types";
import type { Locale } from "../i18n";
import { spriteMarkup } from "./assets";

export interface RunResultSummary {
  outcome: "victory" | "defeat";
  wave: number;
  dangerLevel: number;
  endless: boolean;
  durationSeconds: number;
  kills: number;
  materials: number;
  level: number;
  eggId: string;
  weaponCount: number;
  weaponSlotLimit: number;
  itemCount: number;
}

export function summarizeRun(
  state: BattleState,
  victory: boolean,
): RunResultSummary {
  return {
    outcome: victory ? "victory" : "defeat",
    wave: state.wave,
    dangerLevel: state.dangerLevel,
    endless: state.endless,
    durationSeconds: Math.max(0, Math.floor(state.tick / 60)),
    kills: state.player.kills,
    materials: state.player.materialsCollected,
    level: state.player.level,
    eggId: state.eggId,
    weaponCount: state.weapons.length,
    weaponSlotLimit: state.player.weaponSlotLimit,
    itemCount: state.items.length,
  };
}

export function runResultMarkup(
  locale: Locale,
  state: BattleState,
  victory: boolean,
  unlockText = "",
): string {
  const zh = locale === "zh-CN";
  const summary = summarizeRun(state, victory);
  const egg = EGG_BY_ID.get(state.eggId);
  const localizedEgg = egg
    ? localizeEgg(locale, egg)
    : { name: state.eggId, archetype: "", description: "" };
  const minutes = Math.floor(summary.durationSeconds / 60);
  const seconds = summary.durationSeconds % 60;
  const stats = resultStats(locale, state);
  const cleanUnlockText = unlockText.replace(/^\s*·\s*/, "");
  return `
    <div class="result-report ${victory ? "is-victory" : "is-defeat"}">
      <header class="result-heading">
        <div>
          <p>${zh ? "本局结算" : "RUN REPORT"} · D${summary.dangerLevel}${summary.endless ? " · ∞" : ""}</p>
          <h2>${victory ? (zh ? "胜利" : "VICTORY") : (zh ? "战败" : "DEFEAT")}</h2>
          <span>${victory ? (zh ? "蛋壳撑到了最后。" : "The shell held through the final wave.") : (zh ? `倒在第 ${summary.wave} 波。下一次换一种构筑。` : `Fell on wave ${summary.wave}. Rebuild and crack back.`)}</span>
        </div>
        ${spriteMarkup("egg", state.eggId, "result-egg")}
      </header>
      <section class="result-overview">
        ${metric(zh ? "波次" : "WAVE", summary.wave)}
        ${metric(zh ? "时间" : "TIME", `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`)}
        ${metric(zh ? "击杀" : "KILLS", summary.kills)}
        ${metric(zh ? "材料" : "MATERIALS", summary.materials)}
        ${metric(zh ? "等级" : "LEVEL", summary.level)}
      </section>
      <section class="result-columns">
        <article class="result-panel result-build-panel">
          <div class="result-section-title"><span>${zh ? "角色与属性" : "EGG & STATS"}</span><b>${localizedEgg.name}</b></div>
          <div class="result-stat-grid">${stats.map(([label, value]) => `<span><i>${label}</i><b>${value}</b></span>`).join("")}</div>
        </article>
        <article class="result-panel">
          <div class="result-section-title"><span>${zh ? "武器" : "WEAPONS"}</span><b>${summary.weaponCount}/${summary.weaponSlotLimit}</b></div>
          <div class="result-weapon-list">${state.weapons.map((weapon) => {
            const definition = WEAPON_BY_ID.get(weapon.definitionId);
            const localized = definition ? localizeWeapon(locale, definition) : { name: weapon.definitionId };
            return `<div style="--result-accent:${definition?.color ?? "#fff"}">${spriteMarkup("weapon", weapon.definitionId, "result-weapon-art")}<span>${localized.name}</span><b>T${weapon.tier}</b></div>`;
          }).join("") || `<p>${zh ? "没有武器" : "No weapons"}</p>`}</div>
        </article>
        <article class="result-panel">
          <div class="result-section-title"><span>${zh ? "永久物品" : "ITEMS"}</span><b>${summary.itemCount}</b></div>
          <div class="result-item-list">${state.items.map((itemId) => {
            const definition = ITEM_BY_ID.get(itemId);
            const localized = definition ? localizeItem(locale, definition) : { name: itemId };
            return `<span title="${localized.name}">${spriteMarkup("item", itemId, "result-item-art")}</span>`;
          }).join("") || `<p>${zh ? "本局没有获得永久物品" : "No permanent items collected"}</p>`}</div>
        </article>
      </section>
      ${cleanUnlockText ? `<section class="result-unlocks"><i>✦</i><div><b>${zh ? "新进度" : "NEW PROGRESS"}</b><span>${cleanUnlockText}</span></div></section>` : ""}
      <footer class="result-actions">
        <button class="secondary-button" type="button" data-result-title>${zh ? "返回主菜单" : "TITLE"}</button>
        <button class="secondary-button" type="button" data-result-new>${zh ? "新游戏" : "NEW RUN"}</button>
        ${victory && !state.endless ? `<button class="secondary-button" type="button" data-result-endless>${zh ? "继续无尽" : "CONTINUE ENDLESS"}</button>` : ""}
        <button class="primary compact-primary" type="button" data-result-retry>${zh ? "使用相同配置重试" : "RETRY LOADOUT"}</button>
      </footer>
    </div>`;
}

function metric(label: string, value: string | number): string {
  return `<div><span>${label}</span><b>${value}</b></div>`;
}

function resultStats(
  locale: Locale,
  state: BattleState,
): Array<[string, string | number]> {
  const zh = locale === "zh-CN";
  const player = state.player;
  return [
    [zh ? "最大蛋壳" : "MAX SHELL", player.maxHealth],
    [zh ? "恢复" : "REGEN", player.regenerationPerSecond],
    [zh ? "伤害" : "DAMAGE", `${Math.round((player.damageMultiplier - 1) * 100)}%`],
    [zh ? "攻速" : "ATTACK SPEED", `${Math.round((player.attackSpeedMultiplier - 1) * 100)}%`],
    [zh ? "护甲" : "ARMOR", player.armor],
    [zh ? "闪避" : "DODGE", `${Math.round(player.dodgeChance * 100)}%`],
    [zh ? "速度" : "SPEED", Math.round(player.speed)],
    [zh ? "幸运" : "LUCK", player.luck],
    [zh ? "收获" : "HARVEST", Math.round(player.harvesting)],
    [zh ? "工程" : "ENGINEERING", player.engineering],
  ];
}
